/**
 * ProtectedRoute — 인증된 사용자만 접근 가능한 라우트 래퍼.
 * 로딩 중이면 스피너, 미인증이면 /login으로 리다이렉트.
 *
 * AdminRoute — admin role인 경우만 통과, 아니면 /dashboard로 리다이렉트.
 *
 * [버그 수정] 서버 재시작 후 profile이 null일 때 premature redirect 방지:
 * - loading 또는 profileLoading 중이면 spinner 유지
 * - user가 있지만 profile이 null인 경우(fetchProfile 실패 후 재시도 중)도 spinner 유지
 */

import { useEffect, useState } from "react";
import { Navigate, Outlet } from "react-router";
import { useAuth } from "../../context/AuthContext";
import { PrivacyMaskProvider } from "../../context/PrivacyMaskContext";

function Spinner() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
    </div>
  );
}

// profile fetch가 일시 장애로 너무 오래 멈춰 있을 때, 무한 스피너 대신
// 보여주는 복구 안내 화면 (2026-06-10 무한로딩 장애 방어).
function StalledNotice() {
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4 px-4 text-center">
      <p className="text-sm text-gray-500 dark:text-gray-400">
        서버 응답이 지연되고 있어요. 잠시 후 다시 시도해주세요.
      </p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-600"
      >
        새로고침
      </button>
    </div>
  );
}

export default function ProtectedRoute() {
  const { session, user, profile, loading, profileLoading } = useAuth();
  const [stalled, setStalled] = useState(false);

  useEffect(() => {
    if (!loading && !profileLoading && user && profile === null) {
      const t = setTimeout(() => setStalled(true), 12000);
      return () => clearTimeout(t);
    }
    setStalled(false);
  }, [loading, profileLoading, user, profile]);

  // 권한이 든 profile이 오기 전에 자식 라우트를 열면 HomeRedirect가 CRM을 기본값으로
  // 골라, CRM을 끈 정책 전용 계정도 API 10여 개를 403으로 호출한 뒤 다시 튕긴다.
  if (loading || profileLoading) return <Spinner />;

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  if (profile === null) return stalled ? <StalledNotice /> : <Spinner />;

  // 개인정보 가리기 모드는 로그인한 업무 화면 전체에 걸린다. 여기서 감싸는 이유는
  // 고객 본인이 자기 정보를 입력하는 공개 폼(/form/:token 등)이 이 관문 밖에 있어
  // 자동으로 제외되기 때문 — 고객이 자기가 쓴 주민번호를 못 보면 안 된다.
  return (
    <PrivacyMaskProvider>
      <Outlet />
    </PrivacyMaskProvider>
  );
}

/**
 * AdminRoute — admin role인 경우만 통과.
 * 미인증이면 /login, admin이 아니면 /dashboard로 리다이렉트.
 */
export function AdminRoute() {
  const { user, profile, loading, profileLoading } = useAuth();

  // user는 있는데 profile이 12초 넘게 null이면 일시 장애로 보고 복구 안내를 띄운다.
  // (그 전까지는 AuthContext의 retry가 채워줄 수 있으므로 spinner 유지)
  const [stalled, setStalled] = useState(false);
  useEffect(() => {
    if (!loading && !profileLoading && user && profile === null) {
      const t = setTimeout(() => setStalled(true), 12000);
      return () => clearTimeout(t);
    }
    setStalled(false);
  }, [loading, profileLoading, user, profile]);

  // auth 초기 로딩 또는 profile fetch 진행 중
  if (loading || profileLoading) return <Spinner />;

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // user가 있지만 profile이 null: fetchProfile이 일시적으로 실패한 상태.
  // 서버 재시작 등 일시적 장애일 수 있으므로 리다이렉트하지 않고 spinner 유지하되,
  // 너무 오래(12초+) 지속되면 무한 스피너 대신 복구 안내 화면으로 전환한다.
  if (profile === null) return stalled ? <StalledNotice /> : <Spinner />;

  if (profile.role !== "admin") {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}
